import type { Timestamp } from '@bufbuild/protobuf/wkt';
import { timestampDate } from '@bufbuild/protobuf/wkt';
import { Alert, AlertDescription, AlertTitle } from '@sunstone/ui';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { relativeTime } from '@/lib/time';

/**
 * Structural subset shared by GetLastWorkspaceWorkflowRunResponse
 * (workspace-scoped) and GetLastDeploymentWorkflowRunResponse
 * (deployment-scoped) - both are generated as separate proto messages (buf
 * requires a distinct response message per RPC) but carry identical fields,
 * so this banner accepts either without depending on one specific generated
 * type.
 */
export interface WorkflowRunLike {
  workflow?: string;
  workflowId?: string;
  kind?: string;
  status?: string;
  conclusion?: string;
  startedAt?: Timestamp;
  completedAt?: Timestamp;
  runUrl?: string;
}

// GetLastWorkspaceWorkflowRun/GetLastDeploymentWorkflowRun's `workflow` field
// is a raw passthrough of x-forge-installer's `github.workflow_ref`, always
// the dispatched file path (".github/workflows/deploy_bais.yml") regardless
// of whether that workflow's YAML has a `name:` key - map known raw
// identifiers to a friendly label rather than fixing this upstream.
function getWorkflowLabel(run: WorkflowRunLike | undefined): string {
  const raw = run?.workflow ?? '';
  if (raw.includes('weekly_release_promote')) {
    if (run?.workflowId?.startsWith('release-plan-')) return 'Weekly Release Promote (Plan)';
    if (run?.workflowId?.startsWith('release-promote-')) return 'Weekly Release Promote (Apply)';
    return 'Weekly Release Promote';
  }
  if (raw.includes('release_promote_multi')) return 'Multi-Tenant Release Promote';
  if (raw.includes('deploy_bais')) return 'Deploy CSAI';
  if (raw.includes('deploy_mai')) return 'Deploy MAI';
  if (raw.includes('deploy_from_main')) return 'Single Service Deploy';
  if (raw.includes('weekly_release_sync')) return 'Weekly Release Sync';
  if (raw.includes('new_install_centxforge')) return 'Centxforge Install';
  if (raw.includes('new_installation')) return 'Application Install';
  if (raw.includes('new_infra')) return 'Infra Provisioning';
  if (raw.includes('workspace_destroy')) return 'Workspace Destroy';
  return run?.kind || 'A workflow';
}

interface WorkflowRunBannerProps {
  run: WorkflowRunLike | undefined;
  /** Shown after "is running" while in progress — e.g. disabled-actions notice. */
  runningHint?: string;
}

/**
 * "Last workflow run" status banner — shared between WorkspaceDetail (a
 * workspace's own runs) and DeploymentDetail (deployment-scoped runs, e.g. a
 * bulk multi-workspace promote/upgrade). Renders nothing when there's no run
 * to show.
 */
export function WorkflowRunBanner({ run, runningHint }: WorkflowRunBannerProps) {
  if (!run) return null;

  const workflowLabel = getWorkflowLabel(run);
  const isRunning = run.status === 'in_progress';
  const isFailed = !isRunning && ['failure', 'cancelled', 'skipped'].includes(run.conclusion ?? '');

  return (
    <Alert variant={isFailed ? 'destructive' : 'default'}>
      {isFailed ? (
        <AlertCircle className="h-4 w-4" />
      ) : isRunning ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <CheckCircle2 className="h-4 w-4" />
      )}
      <AlertTitle>
        {isRunning
          ? `${workflowLabel} is running`
          : isFailed
            ? `${workflowLabel} failed`
            : `${workflowLabel} succeeded`}
      </AlertTitle>
      <AlertDescription>
        {isRunning && run.startedAt
          ? `Started ${relativeTime(timestampDate(run.startedAt))}.`
          : run.completedAt
            ? `${isFailed ? 'Failed' : 'Completed'} ${relativeTime(timestampDate(run.completedAt))}.`
            : null}
        {isRunning && runningHint && ` ${runningHint}`}
        {run.runUrl && (
          <>
            {' '}
            <a
              href={run.runUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4"
            >
              View in GitHub
            </a>
          </>
        )}
      </AlertDescription>
    </Alert>
  );
}
